import type { CVData } from '../types';

/**
 * Convert CVData to a plain-text resume string suitable for AI API calls.
 * Extracted from App.tsx so wizard steps can use it without importing the whole app.
 */
export function cvDataToText(cv: CVData): string {
  const p = cv.personalInfo;
  const lines: string[] = [];
  const name = [p.civility, p.firstName, p.lastName].filter(Boolean).join(' ');
  if (name) lines.push(name);
  if (p.title) lines.push(p.title);
  if (p.email) lines.push(p.email);
  if (p.phone) lines.push(p.phone);
  if (p.city || p.country || p.location) lines.push([p.city, p.country].filter(Boolean).join(', ') || p.location || '');
  if (p.summary) lines.push('\nRésumé\n' + p.summary);
  if (cv.experience?.length) {
    lines.push('\nExpérience');
    cv.experience.forEach((e) => {
      lines.push(`${e.position} - ${e.company} (${e.startDate ?? ''} - ${e.endDate ?? ''})`);
      if (e.description) lines.push(e.description);
    });
  }
  if (cv.education?.length) {
    lines.push('\nFormation');
    cv.education.forEach((e) => {
      lines.push(`${e.degree} - ${e.school} (${e.date ?? e.endDate ?? ''})`);
    });
  }
  if (cv.skills?.length) lines.push('\nCompétences\n' + cv.skills.join(', '));
  if (cv.languages?.length) lines.push('\nLangues\n' + cv.languages.join(', '));
  if (cv.hobbies?.length) lines.push("\nCentres d'intérêt\n" + cv.hobbies.join(', '));
  return lines.join('\n');
}

/** Deep clone a CVData object, preferring structuredClone when available. */
export function cloneCvData(cv: CVData): CVData {
  if (typeof structuredClone === 'function') {
    return structuredClone(cv);
  }
  return JSON.parse(JSON.stringify(cv)) as CVData;
}

/**
 * Derive a URL-safe, unique template id from a human-readable name.
 * Falls back to "modele" if the name is empty after normalization.
 */
export function makeTemplateId(name: string, existingIds: string[]): string {
  const base =
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'modele';
  let id = base;
  let i = 2;
  while (existingIds.includes(id)) {
    id = `${base}-${i}`;
    i += 1;
  }
  return id;
}
